// E-Sign Via Derwent — Broker admin dashboard.
//
// v2: Displays customer NAMES as the primary identifier (with the doc name
// underneath), status tabs (Pending / Partially Signed / Signed / Completed
// / Voided), advanced filter panel + search, and a "Clear Filters" reset.
// Opening a request navigates to a dedicated full-page detail route so the
// signing status view is no longer trapped in a right-hand drawer.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, Plus, Loader2, Upload, X, FileText, Users, PenLine,
  Search, SlidersHorizontal, User, ChevronRight,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BetaBadge from '../components/BetaBadge';
import { useAuth } from '../contexts/AuthContext';
import { EsignTemplatesPanel } from '../components/esign/EsignTemplatesPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = '#0057FF';

// Inject the drag-target pulse keyframes once at module load.
if (typeof document !== 'undefined' && !document.getElementById('esign-archived-pulse-kf')) {
  const s = document.createElement('style');
  s.id = 'esign-archived-pulse-kf';
  s.textContent = `@keyframes esignArchivedPulse{0%,100%{filter:brightness(1)}50%{filter:brightness(1.18)}}`;
  document.head.appendChild(s);
}

// Status → chip visuals (for the request cards + tabs).
const STATUS_META = {
  draft:            { label: 'Draft',            cls: 'bg-slate-100 text-slate-600',    dot: 'bg-slate-400' },
  sent:             { label: 'Sent',             cls: 'bg-blue-50 text-blue-700',       dot: 'bg-blue-500' },
  viewed:           { label: 'Viewed',           cls: 'bg-amber-50 text-amber-700',     dot: 'bg-amber-500' },
  partially_signed: { label: 'Partially signed', cls: 'bg-amber-50 text-amber-700',     dot: 'bg-amber-500' },
  completed:        { label: 'Completed',        cls: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  voided:           { label: 'Voided',           cls: 'bg-rose-50 text-rose-700',       dot: 'bg-rose-500' },
  expired:          { label: 'Expired',          cls: 'bg-rose-50 text-rose-700',       dot: 'bg-rose-500' },
};

// User-facing tabs. Each maps to one or more backend statuses.
// "Signed" surfaces docs where every signer has signed but the broker
// hasn't finalised (currently rare — we finalise automatically — but the
// tab is exposed so the mapping is future-proof).
const STATUS_TABS = [
  { key: 'all',              label: 'All',              matches: null },
  { key: 'pending',          label: 'Pending',          matches: ['draft', 'sent', 'viewed'] },
  { key: 'partially_signed', label: 'Partially signed', matches: ['partially_signed'] },
  { key: 'signed',           label: 'Signed',           matches: ['signed'] },
  { key: 'completed',        label: 'Completed',        matches: ['completed'] },
  { key: 'voided',           label: 'Voided / Declined', matches: ['voided', 'expired', 'declined'] },
  { key: 'archived',         label: 'Archived',         matches: '__archived__' },
];

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-AU', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—';

const customerOf = (d) => d.customer_name || d.signers?.[0]?.name || d.title || 'Unnamed request';

export default function EsignAdmin() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [useTpl, setUseTpl] = useState(null);
  const location = useLocation();
  const prefill = useMemo(() => { const sp = new URLSearchParams(location.search); if (sp.get('new') !== '1') return null; try { return { customer: sp.get('customer') || '', signers: JSON.parse(sp.get('signers') || '[]'), application_id: sp.get('application_id') || '', hub_client_id: sp.get('hub_client_id') || '', title: sp.get('title') || '' }; } catch { return null; } }, [location.search]);
  useEffect(() => { if (prefill) { try { localStorage.removeItem('esign_create_draft_v1'); } catch (_) { /* noop */ } setShowCreate(true); } }, [prefill]);
  const [showFilters, setShowFilters] = useState(false);
  const [q, setQ] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    customer_name: '',
    signer_name: '',
    broker: '',
    document_name: '',
    date_sent_from: '',
    date_sent_to: '',
    date_completed_from: '',
    date_completed_to: '',
  });

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  }), []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/esign/documents`, { headers: authHeaders });
      setDocs(data.documents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load documents');
    } finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  // Tab counts derived from the full doc list (unfiltered).
  // Archived docs live in their own bucket and do NOT contribute to the
  // other status tab counts (or the "All" count).
  const tabCounts = useMemo(() => {
    const active = docs.filter((d) => !d.archived);
    const archived = docs.filter((d) => d.archived);
    const counts = { all: active.length, archived: archived.length };
    for (const tab of STATUS_TABS) {
      if (tab.key === 'all' || tab.key === 'archived') continue;
      counts[tab.key] = active.filter((d) => (tab.matches || []).includes(d.status)).length;
    }
    return counts;
  }, [docs]);

  const activeTabDef = STATUS_TABS.find((t) => t.key === activeTab);
  const dateInRange = (iso, from, to) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    if (from && t < new Date(from).getTime()) return false;
    if (to && t > new Date(to).getTime() + 86_400_000) return false;
    return true;
  };

  const filtered = useMemo(() => docs.filter((d) => {
    // Archive isolation
    if (activeTab === 'archived') {
      if (!d.archived) return false;
    } else {
      if (d.archived) return false;
      // Tab match (only when we're not in Archived)
      if (activeTabDef?.matches && Array.isArray(activeTabDef.matches) && !activeTabDef.matches.includes(d.status)) return false;
    }
    // Search box — customer, signer name, doc name
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const hit = customerOf(d).toLowerCase().includes(s)
        || (d.title || '').toLowerCase().includes(s)
        || (d.signers || []).some((sg) => (sg.name + sg.email).toLowerCase().includes(s));
      if (!hit) return false;
    }
    // Filter panel
    const f = filters;
    if (f.customer_name && !customerOf(d).toLowerCase().includes(f.customer_name.toLowerCase())) return false;
    if (f.signer_name && !(d.signers || []).some((sg) => sg.name?.toLowerCase().includes(f.signer_name.toLowerCase()))) return false;
    if (f.broker && !(d.broker_name || '').toLowerCase().includes(f.broker.toLowerCase())) return false;
    if (f.document_name && !(d.title || '').toLowerCase().includes(f.document_name.toLowerCase())) return false;
    if (f.date_sent_from || f.date_sent_to) {
      if (!dateInRange(d.sent_at, f.date_sent_from, f.date_sent_to)) return false;
    }
    if (f.date_completed_from || f.date_completed_to) {
      if (!dateInRange(d.completed_at, f.date_completed_from, f.date_completed_to)) return false;
    }
    return true;
  }), [docs, q, activeTab, activeTabDef, filters]);

  // ─── Drag & drop: archive / unarchive ────────────────────────
  const [dragOverTab, setDragOverTab] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Global drag listeners — any card starting a drag inflates the Archived tab
  // so the drop target is impossible to miss.
  useEffect(() => {
    const onStart = (e) => {
      // Only react when an esign card starts dragging (not other DnD)
      if (e.target?.getAttribute?.('data-testid')?.startsWith('esign-doc-row-')) {
        setIsDragging(true);
      }
    };
    const onEnd = () => { setIsDragging(false); setDragOverTab(null); };
    document.addEventListener('dragstart', onStart);
    document.addEventListener('dragend', onEnd);
    document.addEventListener('drop', onEnd);
    return () => {
      document.removeEventListener('dragstart', onStart);
      document.removeEventListener('dragend', onEnd);
      document.removeEventListener('drop', onEnd);
    };
  }, []);

  const setArchived = useCallback(async (docId, archived) => {
    // optimistic update
    setDocs((prev) => prev.map((d) => d.id === docId ? { ...d, archived, archived_at: archived ? new Date().toISOString() : null } : d));
    try {
      await axios.patch(`${API}/api/esign/documents/${docId}/archive`, { archived }, { headers: authHeaders });
      toast.success(archived ? 'Moved to Archived' : 'Restored from Archived');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update archive state');
      load();
    }
  }, [authHeaders, load]);

  const handleTabDrop = (tabKey) => (e) => {
    e.preventDefault();
    setDragOverTab(null);
    const docId = e.dataTransfer.getData('text/esign-doc-id');
    if (!docId) return;
    if (tabKey === 'archived') setArchived(docId, true);
    else setArchived(docId, false);
  };

  const clearFilters = () => {
    setQ('');
    setFilters({
      customer_name: '', signer_name: '', broker: '', document_name: '',
      date_sent_from: '', date_sent_to: '', date_completed_from: '', date_completed_to: '',
    });
    setActiveTab('all');
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length + (q ? 1 : 0);

  return (
    <div className="cp-admin min-h-screen">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Sub-header */}
        <div className="mb-6">
          <button type="button" onClick={() => navigate('/')} data-testid="esign-back"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-3">
            <ChevronLeft className="w-4 h-4" /> Back to CRM
          </button>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center">
                  <PenLine className="w-4 h-4 text-violet-600" strokeWidth={2.2} />
                </span>
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-950 inline-flex items-center gap-2.5 flex-wrap">
                  E-Sign Via Derwent
                  <BetaBadge size="sm" />
                </h1>
              </div>
              <p className="text-sm text-slate-500">Upload documents, send them for signing, and track every event.</p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowTemplates((v) => !v)} data-testid="esign-templates-toggle"
                      className="inline-flex items-center gap-1.5 rounded-full text-sm font-bold px-4 py-2.5 bg-white text-slate-700 border border-slate-300 hover:bg-slate-100">
                <FileText className="w-4 h-4" /> Templates
              </button>
              <button type="button" onClick={() => setShowCreate(true)} data-testid="esign-new-document"
                      className="inline-flex items-center gap-1.5 rounded-full text-sm font-bold px-5 py-2.5 text-white shadow-sm hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: BRAND }}>
                <Plus className="w-4 h-4" /> New request
              </button>
            </div>
          </div>
        </div>

        {showTemplates && <EsignTemplatesPanel authHeaders={authHeaders} onClose={() => setShowTemplates(false)} onUse={(t) => { setUseTpl(t); setShowCreate(true); }} />}

        {/* Status tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 mb-4 -mx-1 px-1" data-testid="esign-tabs">
          {STATUS_TABS.map((tab) => {
            const active = activeTab === tab.key;
            const count = tabCounts[tab.key] ?? 0;
            const isDropTarget = tab.key === 'archived' || (activeTab === 'archived' && tab.key !== 'archived');
            const isOver = dragOverTab === tab.key;
            const isArchivedInflated = tab.key === 'archived' && isDragging;
            return (
              <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)}
                      data-testid={`esign-tab-${tab.key}`}
                      onDragOver={isDropTarget ? (e) => { e.preventDefault(); setDragOverTab(tab.key); } : undefined}
                      onDragLeave={isDropTarget ? () => setDragOverTab(null) : undefined}
                      onDrop={isDropTarget ? handleTabDrop(tab.key) : undefined}
                      style={isArchivedInflated ? {
                        transform: `scale(${isOver ? 1.5 : 1.28})`,
                        boxShadow: isOver
                          ? '0 0 0 4px rgba(139,92,246,0.35), 0 16px 40px -8px rgba(139,92,246,0.55)'
                          : '0 0 0 3px rgba(139,92,246,0.25), 0 10px 28px -6px rgba(139,92,246,0.35)',
                        animation: 'esignArchivedPulse 1.05s ease-in-out infinite',
                      } : undefined}
                      className={`relative inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 whitespace-nowrap transition-all duration-200 border origin-center ${
                        isArchivedInflated
                          ? 'bg-violet-600 text-white border-violet-700 z-10'
                          : active
                            ? 'bg-[#15133A] text-white border-slate-900'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                      } ${isOver && !isArchivedInflated ? 'ring-2 ring-violet-400 border-violet-400 scale-105' : ''}`}>
                {isArchivedInflated && (
                  <span aria-hidden className="mr-1 text-base leading-none">📥</span>
                )}
                {isArchivedInflated ? 'Drop here to archive' : tab.label}
                <span className={`text-[10px] font-black rounded-full px-1.5 py-0.5 ${isArchivedInflated ? 'bg-white/25 text-white' : active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search + filter row */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search customer, signer or document name…"
                   data-testid="esign-search"
                   className="w-full pl-9 pr-3 py-2.5 rounded-2xl outline-none text-sm bg-white border border-slate-200/80 focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
          </div>
          <button type="button" onClick={() => setShowFilters((v) => !v)} data-testid="esign-filter-btn"
                  className={`inline-flex items-center gap-1.5 rounded-2xl text-sm font-bold px-4 py-2.5 whitespace-nowrap border transition-colors ${
                    activeFilterCount || showFilters
                      ? 'bg-[#15133A] text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                  }`}>
            <SlidersHorizontal className="w-4 h-4" /> Filters
            {activeFilterCount > 0 && (
              <span className={`text-[10px] font-black rounded-full px-1.5 py-0.5 ${
                activeFilterCount ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
              }`}>{activeFilterCount}</span>
            )}
          </button>
          {activeFilterCount > 0 && (
            <button type="button" onClick={clearFilters} data-testid="esign-clear-filters"
                    className="inline-flex items-center gap-1.5 rounded-2xl text-sm font-bold px-4 py-2.5 whitespace-nowrap text-slate-600 hover:text-slate-900 hover:bg-slate-100">
              <X className="w-4 h-4" /> Clear filters
            </button>
          )}
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="rounded-3xl p-5 mb-4 bg-white border border-slate-200/80 shadow-sm" data-testid="esign-filter-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <FilterInput label="Customer name" value={filters.customer_name}
                           onChange={(v) => setFilters({ ...filters, customer_name: v })} testid="filter-customer" />
              <FilterInput label="Signer name" value={filters.signer_name}
                           onChange={(v) => setFilters({ ...filters, signer_name: v })} testid="filter-signer" />
              <FilterInput label="Broker / team member" value={filters.broker}
                           onChange={(v) => setFilters({ ...filters, broker: v })} testid="filter-broker" />
              <FilterInput label="Document name" value={filters.document_name}
                           onChange={(v) => setFilters({ ...filters, document_name: v })} testid="filter-doc" />
              <FilterInput label="Date sent from" type="date" value={filters.date_sent_from}
                           onChange={(v) => setFilters({ ...filters, date_sent_from: v })} testid="filter-sent-from" />
              <FilterInput label="Date sent to" type="date" value={filters.date_sent_to}
                           onChange={(v) => setFilters({ ...filters, date_sent_to: v })} testid="filter-sent-to" />
              <FilterInput label="Date completed from" type="date" value={filters.date_completed_from}
                           onChange={(v) => setFilters({ ...filters, date_completed_from: v })} testid="filter-comp-from" />
              <FilterInput label="Date completed to" type="date" value={filters.date_completed_to}
                           onChange={(v) => setFilters({ ...filters, date_completed_to: v })} testid="filter-comp-to" />
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline-block" /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl p-12 text-center bg-white border border-slate-200/80">
            <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4 bg-slate-100">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-lg font-bold text-slate-900">
              {docs.length === 0 ? 'No requests yet' : 'No requests match your filters'}
            </p>
            <p className="text-sm mt-1 text-slate-500">
              {docs.length === 0
                ? 'Upload your first PDF and send it out for signing.'
                : 'Try clearing filters or switching to another status tab.'}
            </p>
            {docs.length === 0 && (
              <button type="button" onClick={() => setShowCreate(true)}
                      className="mt-6 inline-flex items-center gap-1.5 rounded-full text-sm font-bold px-5 py-2.5 text-white shadow-sm hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: BRAND }}>
                <Plus className="w-4 h-4" /> Create your first request
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2.5">
            {filtered.map((d) => <RequestCard key={d.id} doc={d} onOpen={() => navigate(`/esign/document/${d.id}`)} />)}
          </div>
        )}
      </main>

      <Footer />

      {showCreate && (
        <CreateDialog
          authHeaders={authHeaders}
          prefill={prefill}
          initialTemplate={useTpl}
          onClose={() => { setShowCreate(false); setUseTpl(null); }}
          onCreated={(newDoc) => { setShowCreate(false); load(); navigate(`/esign/document/${newDoc.id}`); }}
        />
      )}
    </div>
  );
}

// ─── Filter input ─────────────────────────────────────────────
const FilterInput = ({ label, value, onChange, type = 'text', testid }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
           className="mt-1 w-full px-3 py-2 rounded-xl text-sm outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
  </label>
);

// ─── Request card ─────────────────────────────────────────────
const RequestCard = ({ doc, onOpen }) => {
  const meta = STATUS_META[doc.status] || STATUS_META.draft;
  const signers = doc.signers || [];
  const signedCount = signers.filter((s) => s.status === 'signed').length;
  const customer = customerOf(doc);
  const onDragStart = (e) => {
    e.dataTransfer.setData('text/esign-doc-id', doc.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  return (
    <button type="button" onClick={onOpen}
            draggable
            onDragStart={onDragStart}
            data-testid={`esign-doc-row-${doc.id}`}
            title={doc.archived ? 'Drag to another tab to unarchive' : 'Drag to Archived to archive'}
            className="w-full text-left rounded-2xl p-4 sm:p-5 bg-white border border-slate-200/80 hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5 transition-all flex items-start gap-4 cursor-grab active:cursor-grabbing">
      {/* Customer avatar */}
      <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-black text-sm shrink-0"
           style={{ backgroundColor: BRAND }}>
        {customer?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <p className="text-base sm:text-lg font-black text-slate-950 truncate" data-testid={`esign-customer-${doc.id}`}>
            {customer}
          </p>
          <span className={`inline-flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${meta.cls}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} /> {meta.label}
          </span>
        </div>
        <p className="text-xs text-slate-500 truncate mt-0.5 flex items-center gap-1">
          <FileText className="w-3 h-3 text-slate-400" />
          {doc.title}
          <span className="text-slate-300">·</span>
          <span className="text-[10px] font-mono text-slate-400">{doc.reference_number}</span>
        </p>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-[11px] text-slate-500">
          <span className="flex items-center gap-1 truncate"><Users className="w-3 h-3" /> {signedCount}/{signers.length} signed</span>
          <span className="truncate"><span className="text-slate-400">Sent</span> {doc.sent_at ? fmtDate(doc.sent_at) : '—'}</span>
          <span className="truncate"><span className="text-slate-400">Last activity</span> {doc.last_activity_at ? fmtDate(doc.last_activity_at) : fmtDate(doc.created_at)}</span>
          <span className="truncate flex items-center gap-1"><User className="w-3 h-3 text-slate-400" /> {doc.broker_name}</span>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 self-center" />
    </button>
  );
};

// ─── Create dialog (with customer name) ────────────────────────
// Persists form state to localStorage so the user never loses their entry if
// the browser reloads or the dialog momentarily unmounts. Also uses a
// mousedown-origin check on the backdrop so drag-selecting text that
// releases outside the card no longer closes the dialog.
const CREATE_DRAFT_KEY = 'esign_create_draft_v1';
export const CreateDialog = ({ authHeaders, onClose, onCreated, prefill, initialTemplate }) => {
  const { user } = useAuth();
  const brokerSignerTag = 'broker-self';   // marker to detect the self-signer row
  const brokerName = user?.esign_signer_name || user?.name || user?.full_name || '';
  const brokerEmail = user?.esign_signer_email || user?.email || '';
  const brokerMobile = user?.esign_signer_mobile || user?.phone || '';
  const initial = (() => {
    try {
      const raw = localStorage.getItem(CREATE_DRAFT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  })();
  const [customer, setCustomer] = useState(prefill?.customer || initial?.customer || '');
  const [title, setTitle] = useState(prefill?.title || initial?.title || '');
  const [message, setMessage] = useState(initial?.message || '');
  const [signers, setSigners] = useState(prefill?.signers?.length ? prefill.signers : initial?.signers?.length ? initial.signers : [{ name: '', email: '', mobile: '', role: 'Signer' }]);
  const [selfSign, setSelfSign] = useState(!!initial?.selfSign);
  const [pdfs, setPdfs] = useState([]); // never persist files across reloads
  const pdf = pdfs[0] || null;
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState(null);
  // Existing-customer type-ahead (pre-fills signers from Client Portal files).
  const [custResults, setCustResults] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [custLoading, setCustLoading] = useState(false);
  const [linkedAppId, setLinkedAppId] = useState(prefill?.application_id || '');
  const pickedRef = useRef(true);   // skip search on first render / right after a pick
  useEffect(() => {
    if (pickedRef.current) { pickedRef.current = false; return; }
    const term = (customer || '').trim();
    if (term.length < 2) { setCustResults([]); setCustOpen(false); return undefined; }
    const t = setTimeout(async () => {
      setCustLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/esign/customer-search`, { headers: authHeaders, params: { q: term } });
        setCustResults(data.customers || []); setCustOpen(true);
      } catch (_) { /* ignore */ } finally { setCustLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [customer, authHeaders]);
  const pickCustomer = (c) => {
    pickedRef.current = true;
    setCustomer(c.label);
    setLinkedAppId(c.application_id || '');
    const clientSigners = (c.signers || []).map((s) => ({ name: s.name || '', email: s.email || '', mobile: s.mobile || '', role: s.role || 'Client' }));
    setSigners((cur) => { const broker = cur.find((s) => s._tag === brokerSignerTag); return broker ? [...clientSigners, broker] : clientSigners; });
    setCustOpen(false);
    toast.success(`Pre-filled ${clientSigners.length} signer${clientSigners.length === 1 ? '' : 's'} from the client file`);
  };
  useEffect(() => { axios.get(`${API}/api/esign/templates`, { headers: authHeaders }).then((r) => setTemplates(r.data.templates || [])).catch(() => {}); }, [authHeaders]);
  const applyTemplate = (t) => {
    setTpl(t);
    if (!t) return;
    if (t.title) setTitle(t.title);
    if (t.message) setMessage(t.message);
    setSigners((cur) => (t.signer_roles || ['Client']).map((role, i) => ({ name: cur[i]?.name || '', email: cur[i]?.email || '', role })));
  };
  useEffect(() => { if (initialTemplate) applyTemplate(initialTemplate); }, [initialTemplate]);
  const fileRef = useRef(null);
  const mouseDownOnBackdrop = useRef(false);

  // Persist text fields on every change.
  useEffect(() => {
    try {
      localStorage.setItem(CREATE_DRAFT_KEY, JSON.stringify({ customer, title, message, signers, selfSign }));
    } catch (_) { /* quota */ }
  }, [customer, title, message, signers, selfSign]);

  // Keep the broker self-signer row in sync with the checkbox.
  const toggleSelfSign = () => {
    setSelfSign((prev) => {
      const next = !prev;
      setSigners((sigs) => {
        const withoutSelf = sigs.filter((s) => s._tag !== brokerSignerTag);
        if (next) {
          if (!brokerEmail) {
            toast.error('Your broker profile has no email — cannot add you as a signer');
            return sigs;
          }
          return [...withoutSelf, {
            name: brokerName || 'Broker',
            email: brokerEmail,
            mobile: brokerMobile,
            role: 'Broker',
            is_broker: true,
            _tag: brokerSignerTag,
          }];
        }
        return withoutSelf;
      });
      return next;
    });
  };

  // Keep the self-signer row's identity in sync with the current broker profile —
  // corrects a stale persisted draft or a row captured before /api/auth/me loaded.
  useEffect(() => {
    if (!selfSign) return;
    setSigners((sigs) => {
      let changed = false;
      const next = sigs.map((s) => {
        if (s._tag !== brokerSignerTag) return s;
        if (s.name === (brokerName || 'Broker') && s.email === brokerEmail && s.mobile === brokerMobile) return s;
        changed = true;
        return { ...s, name: brokerName || 'Broker', email: brokerEmail, mobile: brokerMobile };
      });
      return changed ? next : sigs;
    });
  }, [brokerName, brokerEmail, brokerMobile, selfSign]);

  const clearDraft = () => { try { localStorage.removeItem(CREATE_DRAFT_KEY); } catch (_) { /* noop */ } };
  const cancel = () => { onClose(); }; // keep draft on close; explicit "Discard" button clears it

  const addSigner = () => setSigners([...signers, { name: '', email: '', mobile: '', role: 'Signer' }]);
  const removeSigner = (i) => setSigners(signers.filter((_, idx) => idx !== i));
  const updSigner = (i, k, v) => setSigners(signers.map((s, idx) => idx === i ? { ...s, [k]: v } : s));

  const canSubmit = customer.trim() && title.trim() && (pdfs.length || tpl?.pdf_filename)
    && signers.every((s) => s.name.trim() && s.email.trim() && (s.is_broker || s._tag === brokerSignerTag || s.mobile?.trim()));

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('customer_name', customer);
      fd.append('message', message);
      fd.append('signers_json', JSON.stringify(signers.map(({ _tag, ...rest }) => rest)));
      pdfs.forEach((f) => fd.append(tpl ? 'pdf' : 'pdfs', f));
      if (!tpl) fd.append('document_names_json', JSON.stringify(pdfs.map((f) => f.name.replace(/\.pdf$/i, ''))));
      if (linkedAppId || prefill?.application_id) fd.append('application_id', linkedAppId || prefill.application_id);
      if (prefill?.hub_client_id) fd.append('hub_client_id', prefill.hub_client_id);
      const url = tpl ? `${API}/api/esign/templates/${tpl.id}/use` : `${API}/api/esign/documents`;
      const { data } = await axios.post(url, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(tpl && !pdf && (tpl.fields || []).length ? 'Request created from template — fields already placed, review then send' : 'Request created — prepare fields, then send for signing');
      clearDraft();
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create request');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-sm"
         onMouseDown={(e) => { mouseDownOnBackdrop.current = (e.target === e.currentTarget); }}
         onMouseUp={(e) => {
           if (mouseDownOnBackdrop.current && e.target === e.currentTarget) cancel();
           mouseDownOnBackdrop.current = false;
         }}>
      <div className="w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto bg-white"
           onMouseDown={(e) => e.stopPropagation()}
           onMouseUp={(e) => e.stopPropagation()}
           data-testid="esign-create-dialog">
        <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Create new</p>
            <p className="text-xl font-extrabold text-slate-900">Upload &amp; prepare document</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => { clearDraft(); setCustomer(''); setTitle(''); setMessage(''); setSigners([{ name: '', email: '', mobile: '', role: 'Signer' }]); setSelfSign(false); setPdfs([]); toast.success('Draft cleared'); }}
                    data-testid="esign-create-discard"
                    className="text-[11px] font-bold uppercase tracking-widest text-slate-400 hover:text-rose-600 transition-colors px-2 py-1">
              Discard draft
            </button>
            <button type="button" onClick={cancel} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100" data-testid="esign-create-close"><X className="w-4 h-4 text-slate-500" /></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {templates.length > 0 && (
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Start from a template (optional)</span>
              <select value={tpl?.id || ''} onChange={(e) => applyTemplate(templates.find((t) => t.id === e.target.value) || null)} data-testid="esign-create-template"
                      className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300">
                <option value="">— Blank request (upload a PDF) —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · v{t.version} · {(t.signer_roles || []).join(', ')}{t.pdf_filename ? '' : ' · needs PDF'}</option>)}
              </select>
              {tpl && <span className="mt-1 block text-[10.5px] text-violet-700">{tpl.pdf_filename ? `Uses the template PDF (${tpl.pdf_filename}) and ${(tpl.fields || []).length} pre-placed field${(tpl.fields || []).length === 1 ? '' : 's'} — upload a PDF below only to override it.` : 'This template has no PDF — upload one below.'}</span>}
            </label>
          )}
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Customer name *</span>
            <div className="relative mt-1.5" data-testid="esign-customer-combobox">
              <input type="text" value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="esign-create-customer"
                     autoComplete="off"
                     onFocus={() => { if (custResults.length) setCustOpen(true); }}
                     onBlur={() => setTimeout(() => setCustOpen(false), 150)}
                     placeholder="Start typing to search existing clients…"
                     className="w-full px-3 py-2.5 rounded-xl text-sm outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
              {custLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
              {custOpen && custResults.length > 0 && (
                <div className="absolute z-40 mt-1.5 w-full rounded-xl border border-slate-200 bg-white p-1 shadow-xl shadow-slate-900/10 max-h-72 overflow-y-auto" data-testid="esign-customer-results">
                  {custResults.map((c) => (
                    <button type="button" key={c.application_id} onMouseDown={(e) => e.preventDefault()} onClick={() => pickCustomer(c)}
                            data-testid={`esign-customer-option-${c.application_id}`}
                            className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-slate-100">
                      <User className="mt-0.5 w-3.5 h-3.5 shrink-0 text-slate-400" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-slate-800">{c.label}</span>
                        <span className="block truncate text-[11px] text-slate-500">{c.reference ? `${c.reference} · ` : ''}{c.signers.length} applicant{c.signers.length === 1 ? '' : 's'} on file{c.signers[0]?.email ? ` · ${c.signers[0].email}` : ''}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="mt-1 block text-[10.5px] text-slate-400">Search an existing client to auto-fill their signers, or type a new name.</span>
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Document title *</span>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="esign-create-title"
                   placeholder="e.g. Credit Guide and Privacy Consent"
                   className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Message to signers (optional)</span>
            <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="esign-create-message"
                      placeholder="Anything you'd like the signer to know when they open the document."
                      className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
          </label>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">PDF file{tpl ? '' : 's'} {tpl?.pdf_filename ? '(from template)' : '*'}</span>
            <div onClick={() => fileRef.current?.click()} data-testid="esign-create-drop"
                 className="mt-1.5 rounded-xl border-2 border-dashed border-slate-300 p-6 text-center cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors">
              <input ref={fileRef} type="file" accept="application/pdf" multiple={!tpl} className="hidden"
                     data-testid="esign-create-file"
                     onChange={(e) => { const picked = Array.from(e.target.files || []); setPdfs((cur) => (tpl ? picked.slice(0, 1) : [...cur, ...picked].slice(0, 10))); e.target.value = ''; }} />
              <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" />
              <p className="text-sm font-bold text-slate-900">{pdfs.length ? `${pdfs.length} document${pdfs.length === 1 ? '' : 's'} in this request` : tpl?.pdf_filename ? `Template PDF: ${tpl.pdf_filename}` : 'Choose one or more PDFs'}</p>
              <p className="text-[11px] mt-0.5 text-slate-500">{tpl ? 'Max 20 MB · PDF only' : 'Bundle several PDFs (e.g. Credit Proposal + NCCP pack) — each applicant gets one progress bar across the whole package. Max 20 MB each.'}</p>
            </div>
            {pdfs.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="esign-create-doc-list">
                {pdfs.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs" data-testid={`esign-create-doc-${i}`}>
                    <FileText className="w-3.5 h-3.5 text-slate-400" /><span className="font-bold text-slate-800 truncate flex-1">{i + 1}. {f.name}</span><span className="text-slate-400">{Math.round(f.size / 1024)} KB</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPdfs((cur) => cur.filter((_, j) => j !== i)); }} className="text-slate-400 hover:text-rose-600" data-testid={`esign-create-doc-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            {/* Broker self-sign toggle */}
            <button type="button" onClick={toggleSelfSign}
                    data-testid="esign-self-sign-toggle"
                    className={`w-full mb-3 flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition-colors text-left ${
                      selfSign
                        ? 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                  selfSign ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  <PenLine className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <p className={`text-sm font-bold ${selfSign ? 'text-emerald-900' : 'text-slate-900'}`}>I will also sign this document</p>
                  <p className={`text-[11px] mt-0.5 truncate ${selfSign ? 'text-emerald-700' : 'text-slate-500'}`}>
                    {selfSign
                      ? `Added as a signer: ${brokerName || 'You'} · ${brokerEmail || 'no email on profile'}`
                      : 'Add yourself as an additional signer alongside the client(s)'}
                  </p>
                </div>
              </div>
              <span className={`shrink-0 w-11 h-6 rounded-full relative transition-colors ${selfSign ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${selfSign ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </span>
            </button>

            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Signers *</span>
              <button type="button" onClick={addSigner} data-testid="esign-add-signer"
                      className="text-xs font-bold inline-flex items-center gap-1" style={{ color: BRAND }}>
                <Plus className="w-3 h-3" /> Add signer
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {signers.map((s, i) => {
                const isBroker = s._tag === brokerSignerTag || s.is_broker;
                return (
                <div key={i} className={`rounded-xl p-3 grid grid-cols-2 gap-2 border ${isBroker ? 'bg-emerald-50/60 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
                     data-testid={`esign-signer-row-${i}`}>
                  {isBroker && (
                    <div className="col-span-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                      <PenLine className="w-3 h-3" /> Broker · internal signer (auto-signed)
                    </div>
                  )}
                  <input type="text" value={s.name} onChange={(e) => updSigner(i, 'name', e.target.value)}
                         placeholder="Signer name" data-testid={`esign-signer-name-${i}`} readOnly={isBroker}
                         className={`px-3 py-2 rounded-lg text-sm outline-none border border-slate-200 focus:border-slate-300 focus:ring-2 focus:ring-blue-100 ${isBroker ? 'bg-emerald-100/50 text-emerald-900 cursor-not-allowed' : 'bg-white'}`} />
                  <input type="email" value={s.email} onChange={(e) => updSigner(i, 'email', e.target.value)}
                         placeholder="Signer email" data-testid={`esign-signer-email-${i}`} readOnly={isBroker}
                         className={`px-3 py-2 rounded-lg text-sm outline-none border border-slate-200 focus:border-slate-300 focus:ring-2 focus:ring-blue-100 ${isBroker ? 'bg-emerald-100/50 text-emerald-900 cursor-not-allowed' : 'bg-white'}`} />
                  <input type="tel" value={s.mobile || ''} onChange={(e) => updSigner(i, 'mobile', e.target.value)}
                         placeholder={isBroker ? 'Mobile (optional)' : 'Mobile number *'} data-testid={`esign-signer-mobile-${i}`}
                         className="px-3 py-2 rounded-lg text-sm outline-none bg-white border border-slate-200 focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
                  <input type="text" value={s.role} onChange={(e) => updSigner(i, 'role', e.target.value)}
                         placeholder="Role (e.g. Client, Guarantor)" readOnly={isBroker} data-testid={`esign-signer-role-${i}`}
                         className={`px-3 py-2 rounded-lg text-sm outline-none border border-slate-200 focus:border-slate-300 focus:ring-2 focus:ring-blue-100 ${isBroker ? 'bg-emerald-100/50 text-emerald-900 cursor-not-allowed' : 'bg-white'}`} />
                  {!isBroker && (
                    <p className="col-span-2 text-[10.5px] text-slate-400 -mt-0.5">The signer verifies this mobile by SMS before the document opens.</p>
                  )}
                  {!isBroker && signers.length > 1 && (
                    <button type="button" onClick={() => removeSigner(i)} className="text-xs font-bold text-rose-600 justify-self-end col-span-2">
                      Remove
                    </button>
                  )}
                </div>
                );
              })}
            </div>
          </div>
          <button type="button" onClick={submit} disabled={!canSubmit || busy} data-testid="esign-create-submit"
                  className="w-full rounded-full text-sm font-bold py-3 mt-2 text-white shadow-sm hover:opacity-90 disabled:opacity-50 transition-opacity"
                  style={{ backgroundColor: BRAND }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save draft'}
          </button>
          <p className="mt-2 text-center text-[11px] text-slate-400">Nothing is sent yet. Next you’ll place the signature fields, then click <span className="font-semibold text-slate-600">Send for signing</span>.</p>
        </div>
      </div>
    </div>
  );
};
